import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useLocation as useSavedLocation } from "@/context/LocationContext";
import { findCity } from "@/data/saudi_locations";
import { BRAND_LOGOS } from "@/data/brand_logos";
import { ChevronLeft, ChevronRight, X, Search } from "lucide-react";
import { toast } from "sonner";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_ID = "jayek_restaurants_map";

// Restaurant teardrop pin with brand logo
function RestaurantPin({ logo, name }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  const fallbackSvg = `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 60'><rect width='60' height='60' rx='30' fill='#E05D3A'/><text x='50%' y='58%' text-anchor='middle' font-family='Arial' font-size='32' font-weight='800' fill='white'>${initial}</text></svg>`
  )}`;
  return (
    <div style={{ position: "relative", width: 48, height: 56, filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.25))" }}>
      <div style={{ position: "absolute", inset: 0, background: "#FFFFFF", border: "2.5px solid #E05D3A", borderRadius: "50% 50% 50% 0", transform: "rotate(-45deg)" }} />
      <div style={{ position: "absolute", top: 4, left: "50%", transform: "translateX(-50%)", width: 36, height: 36, background: "#FFFFFF", borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img src={logo || fallbackSvg} alt="" style={{ width: 28, height: 28, objectFit: "contain" }} />
      </div>
    </div>
  );
}

// Blue "you are here" dot — pushed down so its center sits on the point
function UserPin() {
  return (
    <div style={{ transform: "translateY(50%)", width: 22, height: 22 }}>
      <div style={{ width: 22, height: 22, background: "#2B7BFF", borderRadius: "50%", border: "3px solid #FFFFFF", boxShadow: "0 0 0 2px rgba(43,123,255,0.35)" }} />
    </div>
  );
}

// Controls the map imperatively: fit-to-all on load + fly to a target
function MapController({ user, restaurants, fitKey, flyTarget }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google) return;
    const pts = [user, ...restaurants.map((r) => ({ lat: r.lat, lng: r.lng }))];
    if (!pts.length) return;
    if (pts.length === 1) {
      map.panTo(pts[0]);
      map.setZoom(14);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    pts.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 70);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);
  useEffect(() => {
    if (!map || !flyTarget) return;
    map.panTo({ lat: flyTarget.lat, lng: flyTarget.lng });
    map.setZoom(flyTarget.zoom || 16);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyTarget?.key]);
  return null;
}

export default function RestaurantsMapPage() {
  const { lang, isRTL } = useLang();
  const navigate = useNavigate();
  const { location } = useSavedLocation();
  const Chev = isRTL ? ChevronRight : ChevronLeft;

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [userPos, setUserPos] = useState(null); // real GPS [lat, lng]
  const [gpsLoading, setGpsLoading] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const flyKeyRef = useRef(0);

  useEffect(() => {
    api.get(`/restaurants?city=${location.city_id}`)
      .then(({ data }) => setRestaurants(data))
      .finally(() => setLoading(false));
  }, [location.city_id]);

  // Try to silently get real GPS on first mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPos([pos.coords.latitude, pos.coords.longitude]),
      () => { /* silent: keep saved-location fallback */ },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  const requestGPS = () => {
    if (!navigator.geolocation) {
      toast.error(lang === "ar" ? "المتصفح لا يدعم GPS" : "GPS not supported");
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ll = [pos.coords.latitude, pos.coords.longitude];
        setUserPos(ll);
        setGpsLoading(false);
        setFlyTarget({ lat: ll[0], lng: ll[1], zoom: 15, key: ++flyKeyRef.current });
        toast.success(lang === "ar" ? "تم تحديد موقعك" : "Located");
      },
      (err) => {
        setGpsLoading(false);
        const msg = err.code === 1
          ? (lang === "ar" ? "رفض إذن الموقع. اسمح بالوصول من إعدادات المتصفح." : "Permission denied. Allow location in browser settings.")
          : err.code === 2
          ? (lang === "ar" ? "تعذّر الوصول للموقع." : "Position unavailable.")
          : (lang === "ar" ? "انتهى الوقت، حاول مرة أخرى." : "Timed out, try again.");
        toast.error(msg, { duration: 6000 });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const userCityCenter = useMemo(() => {
    const c = findCity(location.city_id);
    return c ? [c.lat, c.lng] : [location.lat, location.lng];
  }, [location.city_id, location.lat, location.lng]);

  // Build a flat list of map pins. Each store with real lat/lng contributes its
  // main location plus any extra branches — every pin links to the same store.
  // Stores without coordinates stay in the normal list but are hidden from the map.
  const locations = useMemo(() => {
    const filter = q.trim().toLowerCase();
    const source = filter
      ? restaurants.filter((r) =>
          (r.name_ar || "").toLowerCase().includes(filter) ||
          (r.name_en || "").toLowerCase().includes(filter) ||
          (r.cuisine_ar || "").toLowerCase().includes(filter) ||
          (r.cuisine_en || "").toLowerCase().includes(filter)
        )
      : restaurants;
    const pins = [];
    source
      .filter((r) => typeof r.lat === "number" && typeof r.lng === "number")
      .forEach((r) => {
        const logo = BRAND_LOGOS[r.id] || r.image;
        pins.push({ ...r, logo, lat: r.lat, lng: r.lng, locId: `${r.id}-main`, branchLabel: "" });
        (r.branches || []).forEach((b, i) => {
          if (typeof b.lat === "number" && typeof b.lng === "number") {
            pins.push({
              ...r,
              logo,
              lat: b.lat,
              lng: b.lng,
              locId: `${r.id}-b${i}`,
              branchLabel: (lang === "ar" ? b.label_ar : b.label_en) || "",
            });
          }
        });
      });
    return pins;
  }, [restaurants, q, lang]);

  const userLatLng = userPos
    ? { lat: userPos[0], lng: userPos[1] }
    : { lat: location.lat, lng: location.lng };

  const focusLocation = (loc) => {
    setSelected(loc);
    setFlyTarget({ lat: loc.lat, lng: loc.lng, zoom: 16, key: ++flyKeyRef.current });
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col z-[9998]">
      {/* Header (respects iOS status-bar safe area) */}
      <div
        className="relative bg-white border-b border-[#EAEAEA] flex items-center justify-center"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 12px)", paddingBottom: "12px" }}
      >
        <button
          onClick={() => navigate(-1)}
          className="absolute start-4 bottom-2.5 w-8 h-8 rounded-full bg-[#F2F2F2] hover:bg-[#EAEAEA] flex items-center justify-center"
          data-testid="map-back"
        >
          <Chev className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-[16px] text-[#1A1A1A]">
          {lang === "ar" ? "خريطة المطاعم" : "Restaurants map"}
        </h1>
      </div>

      {/* Search bar */}
      <div className="bg-white px-4 py-2 border-b border-[#EAEAEA] z-10">
        <div className="flex items-center gap-2 bg-[#F5F5F5] rounded-full px-4 py-2.5">
          <Search className="w-4 h-4 text-[#6B6B6B] flex-shrink-0" />
          <input
            type="text"
            placeholder={lang === "ar" ? `ابحث في ${location.city_ar}` : `Search in ${location.city_en}`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-[#9A9A9A]"
            data-testid="map-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-[#EAEAEA]">
              <X className="w-3.5 h-3.5 text-[#6B6B6B]" />
            </button>
          )}
          <span className="text-[11px] font-semibold text-[#E05D3A] bg-white rounded-full px-2 py-0.5" data-testid="count-badge">
            {locations.length}
          </span>
        </div>
      </div>

      {/* Map */}
      <div className="relative flex-1 min-h-0">
        {!GOOGLE_MAPS_API_KEY ? (
          <div className="h-full w-full flex items-center justify-center text-sm text-[#6B6B6B] bg-[#FAFAFA]">
            خدمة الخرائط غير متاحة
          </div>
        ) : (
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY} language={lang === "ar" ? "ar" : "en"} region="SA">
            <Map
              defaultCenter={{ lat: userCityCenter[0], lng: userCityCenter[1] }}
              defaultZoom={13}
              mapId={MAP_ID}
              gestureHandling="greedy"
              streetViewControl={false}
              mapTypeControl={false}
              fullscreenControl={false}
              zoomControl={false}
              clickableIcons={false}
              style={{ width: "100%", height: "100%" }}
              data-testid="restaurants-map"
            >
              <MapController
                user={userLatLng}
                restaurants={locations}
                fitKey={`${userPos ? "gps" : "saved"}-${locations.length}`}
                flyTarget={flyTarget}
              />

              {/* User location pin */}
              <AdvancedMarker position={userLatLng}>
                <UserPin />
              </AdvancedMarker>

              {/* Restaurant / branch pins */}
              {locations.map((loc) => (
                <AdvancedMarker
                  key={loc.locId}
                  position={{ lat: loc.lat, lng: loc.lng }}
                  onClick={() => focusLocation(loc)}
                >
                  <RestaurantPin logo={loc.logo} name={lang === "ar" ? loc.name_ar : loc.name_en} />
                </AdvancedMarker>
              ))}
            </Map>
          </APIProvider>
        )}

        {loading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-[1000]">
            <div className="w-10 h-10 rounded-full border-4 border-[#E05D3A] border-t-transparent animate-spin" />
          </div>
        )}

        {/* My-location floating button — fetches real GPS */}
        <button
          type="button"
          onClick={requestGPS}
          disabled={gpsLoading}
          className="absolute start-4 top-4 z-[400] w-11 h-11 rounded-full bg-white shadow-xl border border-[#EAEAEA] flex items-center justify-center hover:bg-[#FFF1EA] active:scale-95 transition disabled:opacity-60"
          data-testid="map-my-location"
          aria-label={lang === "ar" ? "موقعي" : "My location"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E05D3A" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={gpsLoading ? "animate-pulse" : ""}><circle cx="12" cy="12" r="3"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2"/></svg>
        </button>

        {/* Horizontal scrollable restaurant cards */}
        <div className="absolute inset-x-0 bottom-0 z-[500] px-2 pb-3 pt-2 bg-gradient-to-t from-white/95 via-white/60 to-transparent">
          <div className="flex gap-2 overflow-x-auto hide-scrollbar snap-x snap-mandatory">
            {locations.map((loc) => (
              <button
                key={loc.locId}
                onClick={() => focusLocation(loc)}
                className={`snap-center flex-shrink-0 w-[260px] bg-white rounded-2xl border ${selected?.locId === loc.locId ? "border-[#E05D3A] shadow-lg" : "border-[#EAEAEA] shadow-sm"} p-2.5 flex items-center gap-2.5 text-start hover:border-[#E05D3A]/40 active:scale-[0.98] transition`}
                data-testid={`map-card-${loc.locId}`}
              >
                <div className="w-12 h-12 rounded-xl bg-white border border-[#F0F0F0] flex-shrink-0 overflow-hidden flex items-center justify-center p-1">
                  <img
                    src={loc.logo}
                    alt={loc.name_ar}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[13px] text-[#1A1A1A] truncate">
                    {lang === "ar" ? loc.name_ar : loc.name_en}
                    {loc.branchLabel ? ` · ${loc.branchLabel}` : ""}
                  </p>
                  <p className="text-[11px] text-[#6B6B6B] truncate">{lang === "ar" ? loc.cuisine_ar : loc.cuisine_en}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-[10px] bg-[#F5F5F5] rounded-full px-1.5 py-0.5 font-semibold text-[#1A1A1A]">
                      ⭐ {(loc.rating || 4.5).toFixed(1)}
                    </span>
                    <span className="text-[10px] text-[#6B6B6B]">
                      · {loc.delivery_time || "30-45"} {lang === "ar" ? "د" : "min"}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Selected restaurant bottom-sheet */}
      {selected && (
        <div className="bg-white border-t border-[#EAEAEA] px-4 py-3 z-[600] shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
          <div className="flex items-center gap-3 max-w-3xl mx-auto">
            <div className="w-14 h-14 rounded-2xl bg-white border border-[#F0F0F0] overflow-hidden flex items-center justify-center p-1.5 flex-shrink-0">
              <img src={selected.logo} alt={selected.name_ar} className="w-full h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[15px] text-[#1A1A1A] truncate">
                {lang === "ar" ? selected.name_ar : selected.name_en}
                {selected.branchLabel ? ` · ${selected.branchLabel}` : ""}
              </p>
              <p className="text-[12px] text-[#6B6B6B] truncate">{lang === "ar" ? selected.cuisine_ar : selected.cuisine_en} · ⭐ {(selected.rating || 4.5).toFixed(1)}</p>
            </div>
            <Link
              to={`/restaurant/${selected.id}`}
              className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] active:scale-[0.98] text-white font-bold text-[13px] px-5 py-2.5 transition"
              data-testid="map-order-btn"
            >
              {lang === "ar" ? "اطلب الآن" : "Order now"}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
